import { useTranslation } from "react-i18next"
import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import axios from "axios"

import { Alert, Badge, Button, Input, Table } from "reactstrap"

import { Loading, ResourceError } from "."

// The Azure subscriptions (entitlements) under the plan. Kept separate from
// the create form: it used to live inside it, behind the form's own feature
// gate, so a customer never saw their own subscriptions at all. Admins read
// the admin route for the customer they are viewing, a customer reads their
// own from the session.
const AzureEntitlements = (props) => {
  const user = useSelector((state) => state.user)
  const [entitlements, setEntitlements] = useState(null)
  const [failure, setFailure] = useState(null)

  useEffect(() => {
    if (!props.azurePlanId) return
    if (user.isAdmin && !props.customerId) return
    const url = user.isAdmin
      ? `/customers/${props.customerId}/orders/${props.azurePlanId}/azureentitlements`
      : `/orders/${props.azurePlanId}/azureentitlements`
    axios
      .get(url)
      .then(({ data }) => {
        setEntitlements(data.entitlements || [])
        setFailure(null)
      })
      .catch((err) => {
        setEntitlements([])
        setFailure({
          error: err?.response?.data?.message || err.message,
          errorCode: err?.response?.data?.code || null,
        })
      })
  }, [user.isAdmin, props.customerId, props.azurePlanId, props.refreshKey])

  if (failure) return <ResourceError resource={failure} />
  if (!entitlements || entitlements.length === 0) return null

  return (
    <Table size="sm" className="mb-2">
      <tbody>
        {entitlements.map((e) => (
          <tr key={e.id}>
            <td title={e.id}>{e.friendlyName || e.id}</td>
            <td>
              <Badge color={e.status === "active" ? "success" : "secondary"}>
                {e.status}
              </Badge>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

// Create-new-subscription form, shown when the customer has an active
// Azure plan. Admins always; customers only when the pilot feature flag
// (azureSelfService) is enabled for them and they are a client admin.
const NewAzureSubscription = (props) => {
  const [t] = useTranslation()
  const user = useSelector((state) => state.user)
  const [name, setName] = useState("")
  const [status, setStatus] = useState(0) // 0 idle, 1 creating, 2 ok, 3 fail

  if (!props.canManage) return null
  if (status === 1) return <Loading small />

  return (
    <div className="mt-2">
      {status === 2 && (
        <Alert color="success">{t("_azureSubscriptionCreated")}</Alert>
      )}
      {status === 3 && (
        <Alert color="danger">{t("_azureSubscriptionCreateFailed")}</Alert>
      )}
      <div className="d-flex">
        <Input
          className="mr-2"
          value={name}
          placeholder={t("_newAzureSubscriptionName")}
          onChange={(e) => setName(e.target.value)}
        />
        <Button
          color="primary"
          disabled={!name.trim()}
          onClick={() => {
            setStatus(1)
            axios
              .post("/orders/azuresubscription", {
                displayName: name.trim(),
                customerId: user.isAdmin ? props.customerId : undefined,
              })
              .then(() => {
                setStatus(2)
                setName("")
                if (props.onCreated) props.onCreated()
              })
              .catch(() => setStatus(3))
          }}
        >
          {t("_createAzureSubscription")}
        </Button>
      </div>
    </div>
  )
}

const AzurePlan = (props) => {
  const [t] = useTranslation()
  const { orders } = props
  const user = useSelector((state) => state.user)
  const [status, setStatus] = useState(0) // 0: not started, 1: making an order 2: success 3: failed
  const [created, setCreated] = useState(0) // bumped to reload the list
  // One permission check for both children: the same gate the create and
  // rename routes enforce, so neither control is offered to someone who
  // would only get a 403 from it.
  const [canManage, setCanManage] = useState(false)
  useEffect(() => {
    if (user.isAdmin) {
      setCanManage(true)
      return
    }
    axios
      .get("/features")
      .then(({ data }) =>
        setCanManage(data?.features?.azureSelfService === true)
      )
      .catch(() => setCanManage(false))
  }, [user.isAdmin])
  if (status === 1 || props.loading) {
    return (
      <div>
        <Loading />
      </div>
    )
  } else if (status === 2) {
    return (
      <div>
        <Alert color="success">{t("_azureSuccess")}</Alert>
      </div>
    )
  } else if (status === 3) {
    return (
      <div>
        <Alert color="danger">{t("_error")}</Alert>
      </div>
    )
  }
  // a customer can have several productType "Azure" subscriptions (legacy
  // subs, reservations, the plan itself). Only the actual Azure plan works
  // for entitlement listing/creation - identify it by Microsoft's global
  // Azure plan product id. Fall back to the old first-match behavior.
  const azureOrders = orders.filter(
    (order) => order?.productType?.id === "Azure"
  )
  const azurePlan =
    azureOrders.find((order) =>
      String(order.offerId || "")
        .toUpperCase()
        .startsWith("DZH318Z0BPS6")
    ) || azureOrders[0]
  if (azurePlan) {
    if (azurePlan.status === "suspended") {
      return (
        <div>
          <Button
            color="primary"
            className="btn-block"
            onClick={() => {
              setStatus(1)
              axios
                .post(`/orders/${azurePlan.id}/reactivate`, {
                  customerId: user.isAdmin ? props.customerId : undefined,
                })
                .then(() => {
                  setStatus(2)
                })
                .catch((err) => {
                  setStatus(3)
                })
            }}
          >
            {t("_reactivateAzurePlan")}
          </Button>
        </div>
      )
    } else {
      return (
        <div>
          <p>{t("_alreadyHasAzurePlan")}</p>
          <AzureEntitlements
            customerId={props.customerId}
            azurePlanId={azurePlan.id}
            refreshKey={created}
          />
          <NewAzureSubscription
            customerId={props.customerId}
            azurePlanId={azurePlan.id}
            canManage={canManage}
            onCreated={() => setCreated((n) => n + 1)}
          />
        </div>
      )
    }
  } else {
    return (
      <div>
        <Button
          color="primary"
          className="btn-block"
          onClick={() => {
            setStatus(1)
            axios
              .post(`/orders/azureplan`, {
                customerId: user.isAdmin ? props.customerId : undefined,
              })
              .then(() => {
                setStatus(2)
              })
              .catch((err) => {
                setStatus(3)
              })
          }}
        >
          {t("_buyAzurePlan")}
        </Button>
      </div>
    )
  }
}

export default AzurePlan
